import React, { useState } from 'react'
import { H3, HEADING_TYPES } from '../../../../Heading/index'
import Icon, { ICON_COLORS } from '../../../../Icon/index'
import ChevronUp from '../../../../Icon/icons/ChevronUp/index'
import ChevronDown from '../../../../Icon/icons/ChevronDown/index'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import {
  URL_PLAYLIST_PURCHASES_VIDEOS,
  URL_PLAYLIST_PURCHASES_EBOOKS,
  URL_PLAYLIST_BONUS_VIDEOS,
  URL_PLAYLIST_BONUS_EBOOKS,
  URL_PLAYLIST_WATCH_HISTORY,
} from '../../../../../services/url/constants'
import { TABS, type CollectionsTab } from '../PlaylistTabs/index'
import { useTranslatedText } from '../../../../StaticText/StaticTextContext'
import scrollToPlaylistSection from '../../shared/scrollToPlaylistSection'

export interface CommunityMyLibraryNavProps {
  history: { push: (path: string) => void }
  location: { pathname?: string }
  activeTab: CollectionsTab
  onTabChange: (tab: CollectionsTab) => void
  hasBonusVideos: boolean
  hasBonusEBooks: boolean
  hasPurchasesVideos: boolean
  hasPurchasesEBooks: boolean
}

const WATCH_LATER = 'watchLater'
const WATCH_HISTORY = 'watchHistory'
const FAVORITES = 'favorites'
type NavAction = CollectionsTab | typeof WATCH_LATER | typeof WATCH_HISTORY | typeof FAVORITES

const BONUS_PURCHASES_PATHS = [
  URL_PLAYLIST_BONUS_VIDEOS,
  URL_PLAYLIST_BONUS_EBOOKS,
  URL_PLAYLIST_PURCHASES_VIDEOS,
  URL_PLAYLIST_PURCHASES_EBOOKS,
]

function CommunityMyLibraryNav ({
  history,
  location,
  activeTab,
  onTabChange,
  hasBonusVideos,
  hasBonusEBooks,
  hasPurchasesVideos,
  hasPurchasesEBooks,
}: CommunityMyLibraryNavProps) {
  const [expanded, setExpanded] = useState(false)

  const libraryTitle = useTranslatedText({ keys: ['libraryNavTitle'] })
  const watchLaterLabel = useTranslatedText({ keys: ['watchLater'] })
  const watchHistoryLabel = useTranslatedText({ keys: ['watchHistory'] })
  const favoritesLabel = useTranslatedText({ keys: ['favorites'] })
  const bonusVideosLabel = useTranslatedText({ keys: ['bonusVideos'] })
  const bonusEBooksLabel = useTranslatedText({ keys: ['bonusEBooks'] })
  const purchasedVideosLabel = useTranslatedText({ keys: ['purchasedVideos'] })
  const purchasedEBooksLabel = useTranslatedText({ keys: ['purchasedEBooks'] })

  const playlistsLabel = useTranslatedText({ keys: ['playlists'] })

  const tabItems: { key: string; label: string; tab: NavAction }[] = [
    { key: 'playlists', label: playlistsLabel, tab: TABS.MY_PLAYLISTS },
    { key: 'watchLater', label: watchLaterLabel, tab: WATCH_LATER },
    { key: 'favorites', label: favoritesLabel, tab: FAVORITES },
    { key: 'history', label: watchHistoryLabel, tab: WATCH_HISTORY },
  ]

  const handleTabClick = (tab: NavAction) => {
    setExpanded(false)
    // Navigate back to /playlist/default when leaving a bonus/purchases view
    if (BONUS_PURCHASES_PATHS.includes(location.pathname as string)) {
      history.push('/playlist/default')
    }
    if (tab === TABS.MY_PLAYLISTS) {
      onTabChange(TABS.MY_PLAYLISTS)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else if (tab === WATCH_LATER) {
      onTabChange(TABS.MY_PLAYLISTS)
      scrollToPlaylistSection('system-playlist-watch-later')
    } else if (tab === WATCH_HISTORY) {
      onTabChange(TABS.MY_PLAYLISTS)
      scrollToPlaylistSection('system-playlist-watch-history')
    } else if (tab === FAVORITES) {
      onTabChange(TABS.MY_PLAYLISTS)
      scrollToPlaylistSection('system-playlist-favorites')
    } else {
      onTabChange(tab)
    }
  }

  const handleLinkClick = (url: string) => {
    setExpanded(false)
    history.push(url)
  }

  // Determine what the collapsed header shows based on active state
  const getCollapsedTitle = (): string => {
    const { pathname } = location
    if (pathname === URL_PLAYLIST_BONUS_VIDEOS) return bonusVideosLabel
    if (pathname === URL_PLAYLIST_BONUS_EBOOKS) return bonusEBooksLabel
    if (pathname === URL_PLAYLIST_PURCHASES_VIDEOS) return purchasedVideosLabel
    if (pathname === URL_PLAYLIST_PURCHASES_EBOOKS) return purchasedEBooksLabel
    const activeItem = tabItems.find(({ tab }) => tab === activeTab)
    if (activeItem) return activeItem.label
    return playlistsLabel
  }

  const contentClassName = [
    'community-my-library-nav__content',
    expanded ? 'community-my-library-nav__content--expanded' : '',
  ].filter(Boolean).join(' ')

  return (
    <div className="community-my-library-nav">
      <Button
        type={BUTTON_TYPES.GHOST}
        className="community-my-library-nav__collapsible"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-label={expanded ? libraryTitle : getCollapsedTitle()}
      >
        <div className="community-my-library-nav__collapsible-heading">
          <H3 as={HEADING_TYPES.H5}>
            {expanded ? libraryTitle : getCollapsedTitle()}
          </H3>
        </div>
        <Icon size={24} color={ICON_COLORS.BLACK}>
          {expanded ? <ChevronUp /> : <ChevronDown />}
        </Icon>
      </Button>

      <div className={contentClassName}>
        <ul className="community-my-library-nav__list">
          {tabItems.map(({ key, label, tab }) => {
            const onBonusPurchasesPage = BONUS_PURCHASES_PATHS.includes(
              location.pathname as string,
            )
            const pathname = (location.pathname as string) || ''
            let isActive: boolean
            if (tab === TABS.MY_PLAYLISTS) {
              isActive = activeTab === TABS.MY_PLAYLISTS && !onBonusPurchasesPage
            } else if (tab === WATCH_HISTORY) {
              isActive = pathname === URL_PLAYLIST_WATCH_HISTORY
            } else {
              isActive = tab !== FAVORITES && activeTab === tab
            }
            return (
              <li key={key} className="community-my-library-nav__tab-item">
                <Button
                  type={BUTTON_TYPES.GHOST}
                  className={[
                    'community-my-library-nav__tab-button',
                    isActive ? 'community-my-library-nav__tab-button--active' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleTabClick(tab)}
                >
                  {label}
                </Button>
              </li>
            )
          })}
          {hasBonusVideos && (
            <li className="community-my-library-nav__tab-item">
              <Button
                type={BUTTON_TYPES.GHOST}
                className={[
                  'community-my-library-nav__tab-button',
                  location.pathname === URL_PLAYLIST_BONUS_VIDEOS ? 'community-my-library-nav__tab-button--active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleLinkClick(URL_PLAYLIST_BONUS_VIDEOS)}
              >
                {bonusVideosLabel}
              </Button>
            </li>
          )}
          {hasBonusEBooks && (
            <li className="community-my-library-nav__tab-item">
              <Button
                type={BUTTON_TYPES.GHOST}
                className={[
                  'community-my-library-nav__tab-button',
                  location.pathname === URL_PLAYLIST_BONUS_EBOOKS ? 'community-my-library-nav__tab-button--active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleLinkClick(URL_PLAYLIST_BONUS_EBOOKS)}
              >
                {bonusEBooksLabel}
              </Button>
            </li>
          )}
          {hasPurchasesVideos && (
            <li className="community-my-library-nav__tab-item">
              <Button
                type={BUTTON_TYPES.GHOST}
                className={[
                  'community-my-library-nav__tab-button',
                  location.pathname === URL_PLAYLIST_PURCHASES_VIDEOS ? 'community-my-library-nav__tab-button--active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleLinkClick(URL_PLAYLIST_PURCHASES_VIDEOS)}
              >
                {purchasedVideosLabel}
              </Button>
            </li>
          )}
          {hasPurchasesEBooks && (
            <li className="community-my-library-nav__tab-item">
              <Button
                type={BUTTON_TYPES.GHOST}
                className={[
                  'community-my-library-nav__tab-button',
                  location.pathname === URL_PLAYLIST_PURCHASES_EBOOKS ? 'community-my-library-nav__tab-button--active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleLinkClick(URL_PLAYLIST_PURCHASES_EBOOKS)}
              >
                {purchasedEBooksLabel}
              </Button>
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}

export default CommunityMyLibraryNav
